import { observer } from 'mobx-react-lite';
import { CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { type SourceKind } from '@/domain/beforered';
import { useT } from '../useT';
import { type ImportSlotStore } from './ImportSlotStore';

interface Props {
  store: ImportSlotStore;
}

export const ImportMappingView = observer(function ImportMappingView({ store }: Props) {
  const t = useT();
  if (store.state !== 'mapping') return null;
  const headers = store.parsed?.headers ?? [];
  const fields = store.fields;
  const ready = store.unmappedRequired.length === 0;

  const labels: Record<SourceKind, string> = {
    BOM: t.slots.bom,
    INVENTORY: t.slots.inventory,
    SALES: t.slots.sales,
    MFG: t.slots.mfg,
    PURCHASE: t.slots.purchase,
    PARTS: t.slots.parts,
  };

  return (
    <div className="border-l-4 border-amber-signal bg-white p-3 text-xs shadow-sm dark:bg-steel-900">
      <header className="mb-2 flex items-center gap-2 font-mono text-xs font-bold tracking-wider text-steel-700 dark:text-steel-200">
        <WarningCircle weight="fill" className="text-amber-signal" size={16} />
        {t.mapping.header} — {labels[store.source]}
      </header>
      <table className="w-full">
        <tbody>
          {fields.map((f) => (
            <tr key={f.name}>
              <td className="py-1 pr-3 font-mono text-steel-500">
                {f.name}
                {f.required && <span className="text-amber-signal">*</span>}
              </td>
              <td className="py-1">
                <select
                  value={store.mapping[f.name] ?? ''}
                  onChange={(e) => store.setMapping(f.name, e.target.value || null)}
                  className="w-full border border-steel-300 bg-white px-2 py-1 font-mono dark:border-steel-700 dark:bg-steel-800"
                >
                  <option value="">— ignore —</option>
                  {headers.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 flex items-center justify-between gap-3 font-mono">
        <span
          className={
            ready ? 'text-light-green-deep dark:text-light-green' : 'text-steel-500'
          }
        >
          {ready ? t.mapping.ready : `${t.mapping.missing}: ${store.unmappedRequired.join(', ')}`}
        </span>
        <button
          type="button"
          onClick={store.confirm}
          disabled={!ready}
          className="flex items-center gap-1 bg-steel-900 px-3 py-1 text-white disabled:opacity-30 dark:bg-steel-100 dark:text-steel-900"
        >
          <CheckCircle weight="fill" size={14} />
          {t.mapping.confirm}
        </button>
      </div>
    </div>
  );
});
